import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { X, Send, Trash2 } from "lucide-react";
import styles from "../../styles/ChatWindow.module.css";
import NonoImage from "../../assets/Nono.png";
import { useAuth } from "../../lib/auth";
import { useNavigate } from "react-router-dom";
import apiClient from "../../Api/apiConfig";

const STORAGE_KEYS = {
  nono: "chatHistory_nono",
  santa: "chatHistory_santa",
};

const ChatWindow = ({ onClose }) => {
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);

  // mode: "nono" (assistant classique) ou "santa"
  const [mode, setMode] = useState("nono");

  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const chatBodyRef = useRef(null);

  // Auth (tel quel : réservé adhérents/admins)
  useEffect(() => {
    if (!isAuthenticated || (user?.role !== "admin" && user?.role !== "member")) {
      alert("Accès réservé aux adhérents et administrateurs.");
      navigate("/login");
    }
  }, [isAuthenticated, user, navigate]);

  // Chargement de l'historique selon le mode
  useEffect(() => {
    const key = STORAGE_KEYS[mode];
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        setChatHistory(JSON.parse(saved));
      } catch (err) {
        console.error("Erreur lors de la récupération de l'historique :", err);
        setChatHistory([]);
      }
    } else {
      setChatHistory([]);
    }
  }, [mode]);

  // Sauvegarde de l'historique selon le mode
  useEffect(() => {
    const key = STORAGE_KEYS[mode];
    localStorage.setItem(key, JSON.stringify(chatHistory));
  }, [chatHistory, mode]);

  // Scroll bas auto
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [chatHistory, loading]);

  const handleSend = async () => {
    if (message.trim() === "") {
      setError("Le message ne peut pas être vide.");
      return;
    }

    setError("");
    const userMessage = { sender: "user", content: message };
    setChatHistory((prev) => [...prev, userMessage]);

    const currentMessage = message;
    setMessage("");
    setLoading(true);

    try {
      let aiText = "";

      if (mode === "nono") {
        // Mode IA classique existant
        const response = await apiClient.post("/api/gemini/generate-letter", {
          prompt: currentMessage,
        });
        aiText =
          response?.data?.letter ||
          "Je n'ai pas pu générer la réponse attendue.";
      } else if (mode === "santa") {
        // Mode Père Noël 🎅
        const senderId = user?.id || 0; // ou autre identifiant
        const response = await apiClient.post("/api/santa/chat", {
          sender_id: senderId,
          message: currentMessage,
        });
        aiText =
          response?.data?.reply ||
          "Ho ho ho ! Père Noël n'a pas entendu, peux-tu répéter mon enfant ?";
      }

      setChatHistory((prev) => [
        ...prev,
        { sender: "ai", content: aiText },
      ]);
    } catch (err) {
      console.error("Erreur :", err);
      setChatHistory((prev) => [
        ...prev,
        {
          sender: "ai",
          content:
            mode === "santa"
              ? "Ho ho ho... Une erreur chez les lutins. Réessaie dans un instant."
              : "Désolé, je n'ai pas pu répondre à votre demande.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !loading) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleClearChat = () => {
    if (
      window.confirm(
        `Effacer toutes les conversations avec ${
          mode === "santa" ? "le Père Noël" : "Nono"
        } ?`
      )
    ) {
      setChatHistory([]);
      localStorage.removeItem(STORAGE_KEYS[mode]);
    }
  };

  const title =
    mode === "santa"
      ? "Chat avec le Père Noël 🎅"
      : "Chat avec l'IA de Nono";

  const tooltipText =
    mode === "santa"
      ? "Je suis le vrai Père Noël (version IA, mais chut 🤫)"
      : "Je suis votre assistant, Nono le petit robot";

  return (
    <div className={styles.window}>
      <div className={styles.header}>
        <div
          className={styles.headerContent}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <img
            src={NonoImage}
            alt="Avatar IA"
            className={styles.robotIcon}
          />
          {showTooltip && (
            <div className={styles.tooltip}>{tooltipText}</div>
          )}
          <h3 className={styles.title}>{title}</h3>
        </div>

        <div className={styles.modeSwitch}>
          <button
            className={
              mode === "nono"
                ? styles.modeButtonActive
                : styles.modeButton
            }
            onClick={() => setMode("nono")}
          >
            🤖 Nono
          </button>
          <button
            className={
              mode === "santa"
                ? styles.modeButtonActive
                : styles.modeButton
            }
            onClick={() => setMode("santa")}
          >
            🎅 Père Noël
          </button>
        </div>

        <div>
          <button
            onClick={handleClearChat}
            className={styles.clearButton}
            aria-label="Effacer les conversations"
          >
            <Trash2 className={styles.icon} />
          </button>
          <button
            onClick={onClose}
            className={styles.closeButton}
            aria-label="Fermer le chat"
          >
            <X className={styles.icon} />
          </button>
        </div>
      </div>

      <div className={styles.body} ref={chatBodyRef}>
        {chatHistory.map((entry, index) => (
          <div
            key={index}
            className={
              entry.sender === "user"
                ? styles.userMessage
                : styles.aiMessage
            }
          >
            <div className={styles.messageBubble}>{entry.content}</div>
          </div>
        ))}
        {loading && (
          <p className={styles.loading}>
            {mode === "santa"
              ? "Le Père Noël prépare sa réponse..."
              : "Nono est en train de répondre..."}
          </p>
        )}
      </div>

      <div className={styles.footer}>
        {error && <p className={styles.error}>{error}</p>}
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          className={styles.input}
          placeholder={
            mode === "santa"
              ? "Écris ton message au Père Noël..."
              : "Écrivez votre message..."
          }
          disabled={loading}
        />
        <button
          className={styles.sendButton}
          onClick={handleSend}
          aria-label="Envoyer le message"
          disabled={loading}
        >
          <Send className={styles.sendIcon} />
        </button>
      </div>
    </div>
  );
};

ChatWindow.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default ChatWindow;
